import { appData, state } from "../../app/state/state.js";
import { updateUserDocument } from "./settingsApi.js";
import {
    getNotificationEventLabel,
    getRequiredNotificationConfigEntries,
} from "../../app/notifications/notificationRegistryAdapter.js";
import {
    getNotificationSettingsGroups,
    getNotificationTimingEvents,
} from "../../app/notifications/notificationSettingsModel.js";
import {
    escapeHTML,
} from "../../shared/utils/contentUtils.js";
import {
    destroyRegisteredControlsIn,
} from "../../shared/ui/controls/controlCore.js";
import {
    NATIVE_INPUT_CLASSES,
} from "../../shared/forms/nativeFieldStyles.js";

let saveTimeout;

function mergeConfiguredNotificationEntry(existingEntry, requiredEntry) {
    if (!requiredEntry) {return existingEntry;}

    const merged = { ...requiredEntry };
    Object.keys(requiredEntry).forEach((field) => {
        if (existingEntry?.[field] !== undefined) {
            merged[field] = existingEntry[field];
        }
    });
    return merged;
}

/**
 * Returns normalized notification controls/defaults with required events present.
 *
 * @param {object} configData Source config object.
 * @param {boolean} [mutate=false] Whether to mutate the source object.
 * @returns {{controls: object, defaults: object}} Normalized config maps.
 */
export function getNotificationConfigWithRequiredEvents(configData, mutate = false) {
    const controlsSource = configData?.adminNotificationControls || {};
    const defaultsSource = configData?.adminNotificationDefaults || {};

    const controls = mutate ? controlsSource : { ...controlsSource };
    const defaults = mutate ? defaultsSource : { ...defaultsSource };

    const requiredEvents = getRequiredNotificationConfigEntries();
    Object.entries(requiredEvents).forEach(([key, value]) => {
        if (value.control) {
            controls[key] = mergeConfiguredNotificationEntry(
                controls[key],
                value.control,
            );
        }
        if (value.default) {
            defaults[key] = mergeConfiguredNotificationEntry(
                defaults[key],
                value.default,
            );
        }
    });

    if (mutate && configData) {
        configData.adminNotificationControls = controls;
        configData.adminNotificationDefaults = defaults;
    }

    return { controls, defaults };
}

/**
 * Returns a user-facing label for a notification event id.
 *
 * @param {string} eventId Notification event id.
 * @returns {string} Display label.
 */
export function getEventLabel(eventId) {
    return getNotificationEventLabel(eventId);
}

function debouncedSave() {
    clearTimeout(saveTimeout);
    saveTimeout = setTimeout(() => {
        const settingsToSave = state.currentUserProfile?.notificationSettings || {};
        const remindersToSave = state.currentUserProfile?.notificationReminders || {};

        updateUserDocument({
            notificationSettings: settingsToSave,
            notificationReminders: remindersToSave,
        });
    }, 1000);
}

function resolveSetting(event, type, controls, defaults, userSettings) {
    const control = controls[event];
    const defaultSetting = defaults[event];
    if (!control || !defaultSetting) {return false;}
    if (control.visibility === "hidden") {return defaultSetting[type];}
    if (control[type] === "locked_on") {return true;}
    if (control[type] === "locked_off") {return false;}
    const userPref = userSettings[event]?.[type];
    if (userPref !== undefined) {return userPref;}
    return defaultSetting[type];
}

function renderControl(event, type, controlType, isChecked) {
    const checkedAttr = isChecked ? "checked" : "";
    const isDisabled = controlType !== "user_control";
    const disabledAttr = isDisabled ? "disabled" : "";
    const disabledClass = isDisabled ? "disabled" : "";
    const eventAttr = escapeHTML(event);
    const typeAttr = escapeHTML(type);

    return `
        <label class="toggle-switch ${disabledClass}">
          <input type="checkbox" class="notification-toggle" data-event="${eventAttr}" data-type="${typeAttr}" ${checkedAttr} ${disabledAttr}>
          <span class="slider"></span>
        </label>
    `;
}

/**
 * Renders the user-level notification settings panel inside the notification shade.
 *
 * @param {HTMLElement} container Panel container element.
 * @returns {void}
 */
export function renderPersonalNotificationSettingsPanel(container) {
    const { controls, defaults } = getNotificationConfigWithRequiredEvents(
        appData.config,
    );
    const userProfile = state.currentUserProfile || {};
    const userSettings = userProfile.notificationSettings || {};
    const userReminders = userProfile.notificationReminders || {};
    const groups = getNotificationSettingsGroups(appData.config, {
        controls,
        userProfile,
    });
    const timingEvents = getNotificationTimingEvents(
        appData.config,
        controls,
        userProfile,
    );

    const renderConfigInput = (label, event) => {
        if (controls[event]?.visibility === "hidden") {
            return "";
        }

        const control = controls[event];
        const isDisabled = control?.days !== "user_control";
        const defaultValue = defaults[event]?.days || 0;
        const userValue = userReminders[event]?.days;
        const finalValue = isDisabled
            ? defaultValue
            : (userValue !== undefined ? userValue : defaultValue);
        const escapedLabel = escapeHTML(label);
        const escapedEvent = escapeHTML(event);

        return `
            <div class="personal-notification-row">
              <span class="personal-notification-label">${escapedLabel}</span>
              <div class="personal-notification-controls personal-notification-number-control">
                <input
                  type="number"
                  min="0"
                  max="60"
                  class="reminder-input no-spinner personal-notification-input ${NATIVE_INPUT_CLASSES}"
                  data-event="${escapedEvent}"
                  value="${finalValue}"
                  ${isDisabled ? "disabled" : ""}
                >
                <span>days</span>
              </div>
            </div>
        `;
    };

    let groupsHtml = "";
    let visibleGroupCount = 0;

    groups.forEach((group) => {
        const visibleGroupEvents = group.events.filter((event) =>
            controls[event.id] &&
            controls[event.id].visibility === "visible"
        );
        if (visibleGroupEvents.length === 0) {return;}

        const sectionClass = visibleGroupCount === 0 ? "" : " personal-notification-group--spaced";
        visibleGroupCount += 1;

        const rows = visibleGroupEvents.map((event) => {
            const control = controls[event.id];
            const label = escapeHTML(event.label || getEventLabel(event.id));
            const inAppChecked = resolveSetting(
                event.id,
                "inApp",
                controls,
                defaults,
                userSettings,
            );
            const emailChecked = resolveSetting(
                event.id,
                "email",
                controls,
                defaults,
                userSettings,
            );

            return `
                <div class="personal-notification-row personal-notification-event-row">
                  <span class="personal-notification-label">${label}</span>
                  <div class="personal-notification-controls personal-notification-toggle-controls">
                    ${renderControl(event.id, "inApp", control.inApp, inAppChecked)}
                    ${renderControl(event.id, "email", control.email, emailChecked)}
                  </div>
                </div>
            `;
        }).join("");

        groupsHtml += `
            <div class="settings-group-wrapper personal-notification-group${sectionClass}">
              <div class="settings-section-header personal-notification-section-header">
                <h4>${escapeHTML(group.label)}</h4>
                <hr />
                ${visibleGroupCount === 1
            ? '<div class="personal-notification-column-headers"><span>App</span><span>Email</span></div>'
            : ""}
              </div>
              ${rows}
            </div>
        `;
    });

    destroyRegisteredControlsIn(container);
    container.innerHTML = `
        <div class="settings-panel personal-notification-settings-panel">
          <div class="settings-section personal-notification-section personal-notification-section--first">
            <div class="settings-section-header personal-notification-section-header"><h4>Timing settings</h4><hr /></div>
            ${timingEvents.map((event) =>
        renderConfigInput(event.label || getEventLabel(event.id), event.id)
    ).join("")}
          </div>
          <div class="settings-section events-section">${groupsHtml}</div>
        </div>
    `;

    container.querySelectorAll(".notification-toggle").forEach((toggle) => {
        toggle.addEventListener("change", (event) => {
            const triggerEvent = event.target.dataset.event;
            const type = event.target.dataset.type;
            if (!state.currentUserProfile.notificationSettings) {
                state.currentUserProfile.notificationSettings = {};
            }
            if (!state.currentUserProfile.notificationSettings[triggerEvent]) {
                state.currentUserProfile.notificationSettings[triggerEvent] = {};
            }
            state.currentUserProfile.notificationSettings[triggerEvent][type] =
                event.target.checked;
            debouncedSave();
        });
    });

    container.querySelectorAll(".reminder-input").forEach((input) => {
        input.addEventListener("change", (event) => {
            const triggerEvent = event.target.dataset.event;
            const days = parseInt(event.target.value, 10) || 0;
            if (!state.currentUserProfile.notificationReminders) {
                state.currentUserProfile.notificationReminders = {};
            }
            if (!state.currentUserProfile.notificationReminders[triggerEvent]) {
                state.currentUserProfile.notificationReminders[triggerEvent] = {};
            }
            state.currentUserProfile.notificationReminders[triggerEvent].days =
                days;
            debouncedSave();
        });
    });

}
